<template>
  <!-- equipmentSec start -->
  <div class="flex flex-col gap-2 tablet:gap-0">
    <div class="flex flex-wrap tablet:flex-nowrap items-center justify-between">
      <div class="flex items-start tablet:items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-[#FE5000] mt-1.5 tablet:mt-0"></span>
        <span class="w-10 h-2 rounded-full bg-[#FE5000] mt-1.5 tablet:mt-0"></span>
        <h2 class="m-0 font-lato font-[700] text-[18px] laptop:text-[20px] text-[#092E87]">Enter or update the equipment
          information</h2>
      </div>
      <button
        class="group cursor-pointer w-[30px] laptop:w-[40px] h-[30px] laptop:h-[40px] ml-auto disabled:bg-[#EFEFEF] disabled:flex justify-center items-center rounded-full disabled:cursor-not-allowed"
        @click="resetClicked()" :disabled="!isFormChanged">
        <img src="../../assets/images/reset-form-icon.png" alt="Reset Icon"
          class="w-full h-full hidden laptop:block group-disabled:!hidden">
        <img src="../../assets/images/reset-icon.png" alt="Reset Icon"
          class="w-full h-full block laptop:hidden group-disabled:!hidden">
        <img src="../../assets/images/bx_reset.png" alt="Reset Icon"
          class="w-[24px] h-[24px] hidden group-disabled:block">
      </button>
    </div>

    <div class="grid grid-cols-1 tablet:grid-cols-3 gap-2 tablet:gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999]">Manufacturer</label>
        <select v-model="selectedManufacture"
          class="form-control form-select form-select-sm !rounded-[10px] focus:!border-[#ccc]"
          aria-label=".form-select-sm example" @change="validateField">
          <option value="null" selected disabled>
            Select Manufacturer
          </option>
          <option v-for="(item, idx) in saveHvacConfig != null && saveHvacConfig.manufacturers" :key="idx" :value="item"
            :selected="selectedManufacture != null && item.id == selectedManufacture.id">
            {{ item.title }}
          </option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999]">System Type</label>
        <select v-model="selectedSystemType"
          class="form-control form-select form-select-sm !rounded-[10px] focus:!border-[#ccc]"
          aria-label=".form-select-sm example" @change="validateField">
          <option value="null" selected disabled>
            Select System Type
          </option>
          <option v-for="(item, idx) in saveHvacConfig != null && saveHvacConfig.system_types" :key="idx" :value="item"
            :selected="selectedSystemType != null && item.id == selectedSystemType.id">
            {{ item.name }}
          </option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999]">Product Type</label>
        <select v-model="selectedProductType"
          class="form-control form-select form-select-sm !rounded-[10px] focus:!border-[#ccc]"
          :disabled="selectedSystemType == null" aria-label=".form-select-sm example" @change="validateField">
          <option value="null" selected disabled>
            Select Product Type
          </option>
          <option v-for="(item, idx) in selectedSystemType != null && selectedSystemType.product_types" :key="idx"
            :value="item" :selected="selectedProductType != null && item.id == selectedProductType.id">
            {{ item.name }}
          </option>
        </select>
      </div>
      <div v-if="selectedProductType == null || (selectedProductType != null && selectedProductType.slug != 'cooling')"
        class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999]">Heat Type</label>
        <select v-model="selectedHeatType"
          class="form-control form-select form-select-sm !rounded-[10px] focus:!border-[#ccc]"
          :disabled="selectedProductType == null" aria-label=".form-select-sm example" @change="validateField">
          <option value="null" selected disabled>
            Select Heat Type
          </option>
          <option v-for="(item, idx) in selectedProductType != null && selectedProductType.heat_types" :key="idx"
            :value="item" :selected="selectedHeatType != null && item.id == selectedHeatType.id">
            {{ item.value }}
          </option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999]">{{ getCapacityTitle() }}</label>
        <select v-model="selectedCapacity"
          class="form-control form-select form-select-sm !rounded-[10px] focus:!border-[#ccc]"
          :disabled="selectedProductType == null" aria-label=".form-select-sm example" @change="validateField">
          <option value="null" selected disabled>
            Select {{ getCapacityTitle() }}
          </option>
          <option v-for="(item, idx) in getCapacities()" :key="idx" :value="item"
            :selected="selectedCapacity != null && item.id == selectedCapacity.id">
            {{ getCapacityName(item) }}
          </option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999]">Placement</label>
        <select v-model="selectedPlacement"
          class="form-control form-select form-select-sm !rounded-[10px] focus:!border-[#ccc]"
          :disabled="selectedSystemType == null" aria-label=".form-select-sm example" @change="validateField">
          <option value="null" selected disabled>
            Select Placement
          </option>
          <option v-for="(item, idx) in selectedSystemType != null && selectedSystemType.placements" :key="idx"
            :value="item" :selected="selectedPlacement != null && item.id == selectedPlacement.id">
            {{ item.name }}
          </option>
        </select>
      </div>
      <div v-if="checkSeer()" class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999] placeholder:!text-[#b8b8b8]">Seer Rating</label>
        <input v-model="seer" type="text" placeholder="Enter Seer"
          class="form-control !rounded-[10px] focus:!border-[#ccc]" :step="0.9" @keyup="validateField"
          @paste="disablePaste" @keypress="isNumber($event)">
      </div>
      <div v-if="checkAfue()" class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999] placeholder:!text-[#b8b8b8]">AFUE</label>
        <input v-model="afue" type="text" placeholder="Enter afue"
          class="form-control !rounded-[10px] focus:!border-[#ccc]" :step="0.9" @keyup="validateField"
          @paste="disablePaste" @keypress="isNumber($event)">
      </div>
      <div v-if="checkHspf()" class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999] placeholder:!text-[#b8b8b8]">HSPF</label>
        <input v-model="hspf" type="text" placeholder="Enter hspf"
          class="form-control !rounded-[10px] focus:!border-[#ccc]" :step="0.9" @keyup="validateField"
          @paste="disablePaste" @keypress="isNumber($event)">
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999] placeholder:!text-[#b8b8b8]">Model Number</label>
        <input v-model="model" type="text" placeholder="Enter model"
          class="form-control !rounded-[10px] focus:!border-[#ccc]"
          :disabled="(saveHvacEquipmentInfo != null && saveHvacEquipmentInfo.model != null && saveHvacEquipmentInfo.model.is_disable == 1)"
          @keyup="validateField">
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999] placeholder:!text-[#b8b8b8]">Serial Number</label>
        <input v-model="serial" type="text" placeholder="Enter serial"
          :disabled="(saveHvacEquipmentInfo != null && saveHvacEquipmentInfo.serial != null && saveHvacEquipmentInfo.serial.is_disable == 1)"
          class="form-control !rounded-[10px] focus:!border-[#ccc]" @keyup="validateField">
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-[16px] font-lato text-[#999]">Age / Installation Date</label>
        <date-picker v-model:value="manufacture_date" input-class="disable-input !rounded-[10px]" format="MM/DD/YYYY"
          :placeholder="'Select Date'" :confirm="true" :readonly="false" :clearable="false"
          :disabled-date="disabledBefore" value-type="format" class="focus:!border-[#ccc]" @change="validateField">
            <template #icon-calendar>
              <img src="../../assets/images/calender-icon.svg" alt="calendar" class="w-5 h-5" />
            </template>
        </date-picker>
      </div>
    </div>
    <button :disabled="!btnEnable"
      class="group w-max mx-auto mt-5 cursor-pointer flex justify-center items-center gap-2.5 px-[30px] py-2 rounded-full border-1 border-[#FE5000] mobile:bg-[#FE5000] mobile:text-[#fff] laptop:bg-[#fff] laptop:text-[#FE5000] hover:bg-[#FE5000] hover:text-[#fff] disabled:border-[#CCCCCC] disabled:bg-transparent disabled:text-[#CCCCCC] disabled:cursor-not-allowed"
      @click="storeEquipmentInfoClicked()">
      {{ $route.query.is_edit == 1 ? "Continue" : "Continue Editing" }}
      <div v-if="$route.query.is_edit != 1">
        <img v-if="btnEnable" src="../../assets/images/edit-quote-button-icon.png" alt="Icon" class="w-[26px] h-[26px] hidden laptop:block laptop:group-hover:hidden">
        <img v-if="btnEnable" src="../../assets/images/edit-quote-button-hover-icon.png" alt="Icon" class="w-[26px] h-[26px] block laptop:hidden laptop:group-hover:block">
        <img src="../../assets/images/disabled-pencil.png" alt="Icon" class="w-[26px] h-[26px] hidden group-disabled:block group-disabled:grayscale filter">
      </div> 
    </button>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DatePicker from "vue-datepicker-next";
import "vue-datepicker-next/index.css";
import { useToast } from "vue-toastification";
const toast = useToast();

export default {
  components: {
    DatePicker,
  },

  data() {
    return {
      btnEnable: false,
      selectedManufacture: null,
      selectedSystemType: null,
      selectedProductType: null,
      selectedHeatType: null,
      selectedPlacement: null,
      selectedCapacity: null,
      isPresetSystemSet: false,
      isPresetProductSet: false,
      isPresetHeatSet: false,
      seer: null,
      afue: null,
      hspf: null,
      model: null,
      serial: null,
      manufacture_date: null,
      originalFormData: ''
    };
  },
  computed: {
    ...mapGetters([
      "saveHvacEquipmentInfo",
      "saveHvacConfig",
      "quoteDetail",
      "saveHvacEquipmentInfoStore",
    ]),

    isFormChanged() {
      const current = JSON.stringify({
        selectedManufacture: this.selectedManufacture,
        selectedSystemType: this.selectedSystemType,
        selectedProductType: this.selectedProductType,
        selectedPlacement: this.selectedPlacement,
        selectedHeatType: this.selectedHeatType,
        selectedCapacity: this.selectedCapacity,
        seer: this.seer,
        afue: this.afue,
        hspf: this.hspf,
        serial: this.serial,
        model: this.model,
        manufacture_date: this.manufacture_date,
      });

      return current !== this.originalFormData; // agar change hua to true
    }
  },

  watch: {
    saveHvacEquipmentInfo: function () {
      this.getHvacConfig({
        manual_quote_id: this.quoteDetail.id,
      });
    },

    saveHvacConfig: function () {
      this.setExistingData()
    },

    saveHvacEquipmentInfoStore: function () {
      toast.success("Equipment information updated successfully.");
      this.setCommponent("QuoteItems");
    },

    selectedSystemType: {
      handler: function () {
        if (!this.isPresetSystemSet) {
          this.selectedProductType = null
          this.selectedCapacity = null
          this.selectedPlacement = null
          this.selectedHeatType = null
        } else {
          this.isPresetSystemSet = false
        }
      }
    },

    selectedProductType: {
      handler: function () {
        if (!this.isPresetProductSet) {
          this.selectedCapacity = null
          this.selectedHeatType = null
          this.seer = null
        } else {
          this.isPresetProductSet = false
        }
      }
    },

    selectedHeatType: {
      handler: function () {
        if (!this.isPresetHeatSet) {
          this.afue = null
          this.hspf = null
        } else {
          this.isPresetHeatSet = false
        }
      }
    },

  },

  setUp() { },
  mounted() {
    this.getHvacEquipmentInfo({
      quote_id: this.quoteDetail.id,
    });
  },

  methods: {
    ...mapActions(["getHvacEquipmentInfo", "getHvacConfig", "HvacEquipmentStore", "setCommponent"]),

    resetClicked() {
      this.setExistingData()
    },

    disabledBefore: function (date) {
      const cdate = new Date();
      cdate.setHours(0, 0, 0, 0);
      return date > cdate;
    },

    getCapacities() {
      if (this.selectedProductType != null) {
        if (this.selectedProductType.slug == "heating") {
          return this.saveHvacConfig.btus
        } else {
          return this.saveHvacConfig.tonnages
        }
      } else { return [] }
    },

    getCapacityTitle() {
      if (this.selectedProductType != null) {
        if (this.selectedProductType.slug == "heating") {
          return 'Heating BTUs'
        } else {
          return 'Tonnage'
        }
      } else { return 'Tonnage' }
    },

    getCapacityName(data) {
      if (this.selectedProductType != null) {
        if (this.selectedProductType.slug == "heating") {
          return data.btu
        } else {
          return data.subtitle
        }
      } else ''
    },

    checkSeer() {
      return (this.selectedProductType != null && this.selectedProductType.slug != "heating")
    },

    checkAfue() {
      return (this.selectedHeatType != null &&
        (this.selectedHeatType.slug == "gas" ||
          this.selectedHeatType.slug == "boiler" ||
          this.selectedHeatType.slug == "dual_fuel"))
    },

    checkHspf() {
      return (this.selectedHeatType != null &&
        (this.selectedHeatType.slug == "heat_pump" ||
          this.selectedHeatType.slug == "dual_fuel"))
    },

    disablePaste(event) {
      event.preventDefault();
    },

    isNumber: function (evt) {
      evt = (evt) ? evt : window.event;
      var charCode = (evt.which) ? evt.which : evt.keyCode;
      var value = evt.target.value;
      if (value.length > 4) {
        return evt.preventDefault();
      } else {
        var dotcontains = value.indexOf(".") != -1;
        if (dotcontains)
          if (charCode == 46) return evt.preventDefault();
        if (charCode == 46) return true;
        if (charCode > 31 && (charCode < 48 || charCode > 57))
          return evt.preventDefault();
      }
      return true;
    },


    validateField() {
      if (this.isValid("watcher")) {
        this.btnEnable = true;
      } else {
        this.btnEnable = false;
      }
    },

    isEmpty(data) {
      if (data == undefined) {
        return true
      } else if (data.length > 0) {
        return false
      } else {
        return true
      }

    },

    isValid(action) {
      let status = true;
      let message = "";
      if (this.selectedManufacture == null) {
        status = false;
        message = "Manufacturer field is required.";
      } else if (this.selectedSystemType == null) {
        status = false;
        message = "System Type field is required.";
      } else if (this.selectedProductType == null) {
        status = false;
        message = "Product Type field is required.";
      } else if (this.selectedProductType.slug != 'cooling' && this.selectedHeatType == null) {
        status = false;
        message = "Heat Type field is required.";
      } else if (this.selectedCapacity == null) {
        status = false;
        message = this.getCapacityTitle() + " field is required.";
      } else if (this.selectedPlacement == null) {
        status = false;
        message = "Placement field is required.";
      } else if (this.checkSeer() && this.isEmpty(this.seer)) {
        status = false;
        message = "Seer field is required.";
      } else if (this.checkAfue() && this.isEmpty(this.afue)) {
        status = false;
        message = "Afue field is required.";
      } else if (this.checkHspf() && this.isEmpty(this.hspf)) {
        status = false;
        message = "Hspf field is required.";
      } else if (this.isEmpty(this.model)) {
        status = false;
        message = "Model Number field is required.";
      } else if (this.isEmpty(this.serial)) {
        status = false;
        message = "Serial Number field is required.";
      } else if (this.isEmpty(this.manufacture_date)) {
        status = false;
        message = "Installation Date field is required.";
      }
      if (!status && action == "submit") {
        toast.error(message);
      }

      return status;
    },

    setExistingData() {
      if (this.saveHvacConfig != null) {
        let self = this
        this.selectedManufacture = this.saveHvacConfig.manufacturers.filter(function (item) {
          return (self.saveHvacEquipmentInfo.manufacturer != null && item.id == self.saveHvacEquipmentInfo.manufacturer.id)
        })[0] ?? null

        this.isPresetSystemSet = true
        this.selectedSystemType = this.saveHvacConfig.system_types.filter(function (item) {
          return (self.saveHvacEquipmentInfo.system_type != null && item.id == self.saveHvacEquipmentInfo.system_type.id)
        })[0] ?? null

        this.isPresetProductSet = true
        if (this.selectedSystemType != null) {
          this.selectedProductType = this.selectedSystemType.product_types.filter(function (item) {
            return (self.saveHvacEquipmentInfo.product_type != null && item.id == self.saveHvacEquipmentInfo.product_type.id)
          })[0] ?? null

          this.selectedPlacement = this.selectedSystemType.placements.filter(function (item) {
            return (self.saveHvacEquipmentInfo.placement != null && item.id == self.saveHvacEquipmentInfo.placement.id)
          })[0] ?? null

        } else {
          this.selectedProductType = null
          this.selectedPlacement = null
        }

        this.isPresetHeatSet = true
        if (this.selectedProductType != null) {
          if (this.selectedProductType.slug != "cooling") {
            this.selectedHeatType = this.selectedProductType.heat_types.filter(function (item) {
              return (self.saveHvacEquipmentInfo.heat_type != null && item.id == self.saveHvacEquipmentInfo.heat_type.id)
            })[0] ?? null
          }

          if (this.selectedProductType.slug == "heating") {
            this.selectedCapacity = this.saveHvacConfig.btus.filter(function (item) {
              return (self.saveHvacEquipmentInfo.heating_btu != null && item.id == self.saveHvacEquipmentInfo.heating_btu.id)
            })[0] ?? null
          } else {
            this.selectedCapacity = this.saveHvacConfig.tonnages.filter(function (item) {
              return (self.saveHvacEquipmentInfo.tonnage != null && item.id == self.saveHvacEquipmentInfo.tonnage.id)
            })[0] ?? null
          }
        } else {
          this.selectedHeatType = null
          this.selectedCapacity = null
        }

        this.seer = this.saveHvacEquipmentInfo.seer
        this.afue = this.saveHvacEquipmentInfo.afue
        this.hspf = this.saveHvacEquipmentInfo.hspf

        if (this.saveHvacEquipmentInfo.serial != null) {
          this.serial = this.saveHvacEquipmentInfo.serial.text
        }
        if (this.saveHvacEquipmentInfo.model != null) {
          this.model = this.saveHvacEquipmentInfo.model.text
        }
        if (this.saveHvacEquipmentInfo.manufacture_date.length > 0) {
          this.manufacture_date = this.saveHvacEquipmentInfo.manufacture_date
        } else {
          this.manufacture_date = null
        }

        this.validateField()

        this.originalFormData = JSON.stringify({
          selectedManufacture: this.selectedManufacture,
          selectedSystemType: this.selectedSystemType,
          selectedProductType: this.selectedProductType,
          selectedPlacement: this.selectedPlacement,
          selectedHeatType: this.selectedHeatType,
          selectedCapacity: this.selectedCapacity,
          seer: this.seer,
          afue: this.afue,
          hspf: this.hspf,
          serial: this.serial,
          model: this.model,
          manufacture_date: this.manufacture_date,
        });

      } else {
        this.getHvacConfig({
          manual_quote_id: this.quoteDetail.id,
        });
      }
    },

    storeEquipmentInfoClicked() {
      this.$dialog.show({
        title: "Equipment Information",
        text: "Are you sure you want to update equipment information?",
        onConfirm: () => {
          if (this.isValid("submit")) {
            this.storeCall()
          }
        },
      });
    },

    storeCall() {


      let unit = {
        manufacturer_id: this.selectedManufacture.id,
        type: this.selectedSystemType.name,
        system_type_id: this.selectedSystemType.id,
        product_type_id: this.selectedProductType.id,
        location_id: this.selectedPlacement.id,
        serial: this.serial,
        model: this.model,
        manufacture_date: this.manufacture_date
      }

      if (this.selectedProductType.slug != 'cooling') {
        unit.heat_type_id = this.selectedHeatType.id
      }

      if (this.selectedProductType.slug == 'heating') {
        unit.heating_btu_id = this.selectedCapacity.id
      } else {
        unit.tonnage_id = this.selectedCapacity.id
      }

      if (!this.isEmpty(this.saveHvacEquipmentInfo.unit_id)) {
        unit.id = this.saveHvacEquipmentInfo.unit_id
      }

      if (this.checkSeer()) {
        unit.seer = this.seer
      }

      if (this.checkHspf()) {
        unit.hspf = this.hspf
      }

      if (this.checkAfue()) {
        unit.afue = this.afue
      }

      let payload = {
        quote_id: this.quoteDetail.id,
        units: JSON.stringify([unit])
      }
      this.HvacEquipmentStore(payload)
    }

  },
};
</script>

<style>
.disable-input {
  pointer-events: none;
}

.unitHead {
  position: relative;
  margin-bottom: 3rem;
}

.unitForm {
  border-radius: 6px;
  margin-bottom: 20px;
}

.unitForm h3 {
  font-size: 17px;
  color: #092e87;
  font-weight: normal;
}

.resetPart {
  position: absolute;
  top: -4px;
  right: 0;
}

.resetPart img {
  width: 40px;
  margin-left: 0.5rem;
}

.unitForm h3 span {
  color: #fe5000;
}

.unitForm .form-control {
  height: 45px;
  border-color: #cacfd3;
}

.unitForm .form-control:focus {
  box-shadow: none;
}

.unitForm .mx-input-wrapper {
  position: relative;
  top: -5px;
}

.main-button {
  background: #fe5000;
  border-radius: 8px;
  box-shadow: 0 4px 5px #999;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  padding: 10px 30px;
}

.main-button:active {
  background-color: #092e87;
  color: #fff;
}

.main-button:disabled {
  background-color: #ccc;
}

.unitInfo-heading {
  font-size: 26px;
  font-weight: 500;
  color: #092e87;
  text-align: center;
}

.unitForm .mx-datepicker {
  width: 100%;
}

.unitForm .mx-datepicker input {
  width: 100%;
  height: 40px;
  border-radius: 7px;
  border: none;
  padding: 0 0 0 12px;
}

.mx-icon-calendar {
  color: #092e87;
}

.resetBtn {
  color: #fe5000;
  font-weight: 600;
  cursor: pointer;
}

.resetBtn:active {
  font-weight: 600;
  cursor: pointer;
  color: #092e87;
}
</style>
